<template>
  <div :class="[$style.recommend_product_list, bgColor]">
    <div :class="$style.product_list_wrapper">
      <p>贈る相手とシーンで探す</p>
      <h2>PERSON & SCENE</h2>
      <div :class="$style.navigation_list">
        <AtomsConsumerButton
          v-for="(category, i) in state.groupedProducts"
          :key="`scene-${i}`"
          type="button"
          @click="scrollToId(`scene-${i}`)"
        >
          # {{ category.category_short }}
          <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="down" />
        </AtomsConsumerButton>
      </div>
      <section
        v-for="(category, i) in state.groupedProducts"
        :id="`scene-${i}`"
        :key="`scene-section-${i}`"
        :class="$style.product_list_section"
      >
        <h3>{{ category.category_title }}</h3>
        <div :class="$style.product_list">
          <div v-for="productId in category.product_ids" :key="`scene-${i}-${productId}`">
            <PagePartsProductListItem
              :key="productId"
              :product="getProductItem(productId)"
              is-no-validation
              @open-fav-modal="openFavModal($event, getProductItem(productId))"
            />
          </div>
        </div>
      </section>
    </div>
    <!-- お気に入りモーダル -->
    <MoleculesConsumerModal v-model="isOpenFavModal">
      <ProductSelectFav
        :product="favModalProduct"
        :favorite-list="favoriteListRef"
        :is-processing="isProcessing"
        @on-favorite="onFavoriteByProductList"
        @lift="() => (isOpenFavModal = false)"
      />
    </MoleculesConsumerModal>
  </div>
</template>

<script setup lang="ts">
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { PagePartsProductType } from "~/types/product"
import { usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery } from "~/gql/urql.generated"
import { PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery } from "~/types/type.generated"
import { useFavorite } from "~/composables/useFavorite"

const props = defineProps<{
  productIdAndCategories: NonNullable<PagePartsFragmentFragment["product_id_and_categories"]>
  products: PagePartsProductType[]
  listParts: PagePartsFragmentFragment["list"]
  bgColor?: string
}>()

type State = {
  groupedProducts: {
    category_title: string
    category_short: string
    product_ids: string[]
  }[]
  products: PagePartsProductType[]
  isDisplayCartModal: boolean
  cartModalProduct: PagePartsProductType | null
}
const state: State = reactive({
  groupedProducts: [],
  products: [],
  isDisplayCartModal: false,
  cartModalProduct: null,
})

const { openFavModal, onFavoriteByProductList, isOpenFavModal, favModalProduct, favoriteListRef, isProcessing } =
  useFavorite()

const scrollToId = (id: string): void => {
  const element = document.getElementById(id)
  element?.scrollIntoView({
    behavior: "smooth",
  })
}

const getProductItem = (productId: string) => {
  const ret = state.products.find((product) => product.document_id === productId)
  return ret ?? state.products[0]
}

const deliveryLtPrefectures = ref<
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
>({
  default_prefecture: "",
  prefectures: [],
})
const { data: _calcDeliveryLtPrefecturesData, executeQuery: _calcDeliveryLtPrefecturesExecuteQuery } =
  usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery({
    requestPolicy: "cache-first",
    pause: true,
  })

onMounted(async () => {
  if (_calcDeliveryLtPrefecturesExecuteQuery().isPaused) _calcDeliveryLtPrefecturesExecuteQuery().resume()
  await _calcDeliveryLtPrefecturesExecuteQuery()
  deliveryLtPrefectures.value = _calcDeliveryLtPrefecturesData.value
    ?.publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures ?? {
    default_prefecture: "",
    prefectures: [],
  }

  if (props.products) {
    state.products = props.products.map((product) => {
      const _product = product
      _product.url = ""
      return _product
    })
  }
  // グループ化するためのオブジェクトを初期化
  const groupedProducts: Record<
    string,
    {
      category_title: string
      category_short: string
      product_ids: string[]
    }
  > = {}

  props.productIdAndCategories.forEach((productIdAndCategory) => {
    const { category_title, category_short, product_id } = productIdAndCategory
    if (category_title && category_short && product_id) {
      if (!groupedProducts[category_title]) {
        groupedProducts[category_title] = {
          category_title,
          category_short,
          product_ids: [],
        }
      }
      groupedProducts[category_title].product_ids.push(product_id)
    }
  })
  state.groupedProducts = Object.values(groupedProducts)
})
</script>

<style scoped module lang="scss">
@import "assets/scss/components/RecommendProductListStyle.scss";
</style>
